import React, { memo, useRef, useCallback, useState, useMemo } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FormLabel } from "../FormLabel";
import FormikFieldError from "../FormikFieldError";
import InfoTooltip from "newui/components/InfoTooltip";
import {
	getInputWrapperClasses,
	baseInputClasses,
	normalInputClasses,
	errorInputClasses,
} from "../InputWithLabel/inputStyles";
import "./rich-text-editor.css";
import { cn } from "utils/helpers";

export const clearHTMLTags = (strToSanitize: string) => {

	if (!strToSanitize || strToSanitize.length === 0) {
		return "";
	}

	const myHTML = new DOMParser().parseFromString(strToSanitize, "text/html");
	return myHTML.body.textContent || "";
};

export interface RichTextEditorProps {
	className?: string;
	disabled?: boolean;
	errorText?: string;
	guideLine?: string;
	hasError?: boolean;
	label?: string;
	maxLength?: number;
	minHeight?: string;
	noMargin?: boolean;
	placeholder?: string;
	required?: boolean;
	showCharacterCount?: boolean;
	subTitle?: string;
	testId?: string;
	value: string;
	onChange: (html: string) => void;
}

const MODULES = {
	toolbar: [
		[{ header: [1, 2, 3, false] }],
		[{ size: ["small", false, "large", "huge"] }],
		["bold", "italic", "underline"],
		[{ color: [] }, { background: [] }],
		[{ list: "ordered" }, { list: "bullet" }],
	],
};

const RichTextEditorInner: React.FC<RichTextEditorProps> = ({
	className = "",
	disabled = false,
	errorText,
	guideLine,
	hasError = false,
	label,
	maxLength,
	minHeight = "6rem",
	noMargin,
	placeholder = "Enter text...",
	required,
	showCharacterCount = maxLength !== undefined && maxLength > 0,
	subTitle,
	testId,
	value,
	onChange,
}) => {
	const quillRef = useRef<ReactQuill>(null);

	const handleChange = useCallback(
		(content: string) => {
			const cleanText = clearHTMLTags(content);
			const isEmpty = !cleanText.trim();
			onChange?.(isEmpty ? "" : content);
		},
		[onChange]
	);

	const remainingCharacters = useMemo(() => {
		if ((maxLength ?? 0) === 0) {
			return 0;
		}
		return maxLength - clearHTMLTags(value || "").length;
	}, [value, maxLength]);

	const maxLengthExceeded = showCharacterCount && remainingCharacters < 0;
	const maxLengthErrorText = maxLengthExceeded
		? `Content exceeds maximum length by ${Math.abs(remainingCharacters)} character${Math.abs(remainingCharacters) !== 1 ? "s" : ""}`
		: undefined;
	const displayErrorText = errorText || maxLengthErrorText;
	const hasInputError = !!displayErrorText || hasError;

	return (
		<div
			className={getInputWrapperClasses(hasInputError, noMargin, className)}
			data-testid={testId}
		>
			{label && (
				<FormLabel label={label} required={required}>
					{guideLine && (
						<div className="inline-block">
							<InfoTooltip tooltipText={guideLine} />
						</div>
					)}
				</FormLabel>
			)}
			{subTitle && (
				<p className="-mt-1 mb-3 whitespace-pre-wrap text-sm font-normal text-gray-text-400">
					{subTitle}
				</p>
			)}

			<div
				className={`
					${baseInputClasses}
					${hasInputError ? errorInputClasses : normalInputClasses}
					rich-text-editor-wrapper !p-0
					${disabled ? "cursor-not-allowed bg-gray-300" : ""}
				`}
				style={{ minHeight }}
			>
				<ReactQuill
					ref={quillRef}
					value={value}
					onChange={handleChange}
					modules={MODULES}
					placeholder={placeholder}
					readOnly={disabled}
					className="rich-text-editor-quill font-roboto text-sm [&_.ql-blank::before]:!text-gray-400"
				/>

				{showCharacterCount && maxLength && (
					<div className={cn("text-right text-xs bg-white px-1", remainingCharacters < 0 ? "text-red-600" : "text-gray-500")}>
						{remainingCharacters} character{remainingCharacters !== 1 ? 's' : ''} remaining
					</div>
				)}
			</div>

			<FormikFieldError text={displayErrorText} />
		</div>
	);
};

export const RichTextEditor = memo(RichTextEditorInner);
